/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, type ReactNode } from 'react';
import { toast } from 'sonner';
import { resolveToastPresentation, type ToastType } from './toast-types';

export type { ToastType, ToastPresentation } from './toast-types';
export { TOAST_PRESENTATION, resolveToastPresentation } from './toast-types';

export interface ToastAction {
    label: string;
    onClick: () => void;
}

export interface ToastOptions {
    duration?: number;
    action?: ToastAction;
    cancel?: ToastAction;
    description?: ReactNode;
}

/**
 * Custom hook for toast notifications using sonner.
 *
 * `type` widened from `success | error | info` to the shared feedback vocabulary
 * (CRCB2B-641). Widening a union is additive, so every existing call site keeps
 * working — but note one behavioural change: the default duration is now the type's
 * own (see TOAST_PRESENTATION) rather than a flat 5000ms, so an `error` raised with
 * no explicit duration now persists until dismissed instead of vanishing after 5s.
 * That is the AC's severity-graded timing requirement, and it is the reason the
 * change is worth making rather than defaulting every type to 5s.
 */
export function useToast() {
    const addToast = useCallback((message: string, type: ToastType = 'info', options?: ToastOptions | number) => {
        const presentation = resolveToastPresentation(type);
        // Support legacy API where duration is passed as third parameter.
        // An explicit duration always outranks the type's default.
        const duration = typeof options === 'number' ? options : (options?.duration ?? presentation.duration);
        const toastOptions = typeof options === 'number' ? {} : (options ?? {});

        const sonnerOptions = {
            duration,
            ...((toastOptions.action || toastOptions.cancel) && { closeButton: false as const }),
            ...(toastOptions.action && {
                action: {
                    label: toastOptions.action.label,
                    onClick: toastOptions.action.onClick,
                },
            }),
            ...(toastOptions.cancel && {
                cancel: {
                    label: toastOptions.cancel.label,
                    onClick: toastOptions.cancel.onClick,
                },
            }),
            ...(toastOptions.description && {
                description: toastOptions.description,
            }),
        };

        // Data, not control flow: the map's `method` selects the Sonner emitter, which
        // stamps `data-type` on the element for `overrides/sonner.css` to colour.
        // A fifth type needs a TOAST_PRESENTATION entry and nothing here.
        return toast[presentation.method](message, sonnerOptions);
    }, []);

    return { addToast };
}

// Re-export toast and Toaster from sonner for convenience
export { toast, Toaster } from 'sonner';

// Pre-configured Toaster wrapper with app-wide defaults (position, offset, icons)
export { AppToaster } from './app-toaster';
